/**
 * Worker Configuration
 * 
 * Load and manage Worker configuration from ~/.paean/worker.json
 */

import * as fs from 'fs';
import * as path from 'path';
import { homedir } from 'os';
import type { WorkerConfig, ExecutorType } from './types.js';
import { getConfigDir } from '../utils/config.js';

// ============================================
// Types
// ============================================

/**
 * Worker config file structure
 */
export interface WorkerConfigFile {
    /** Enable Worker mode */
    enabled?: boolean;
    /** Worker capabilities */
    capabilities?: string[];
    /** Poll interval in milliseconds */
    pollInterval?: number;
    /** Task execution timeout in milliseconds */
    taskTimeout?: number;
    /** Enable autonomous mode (no confirmations) */
    autonomousMode?: boolean;
    /** Enable task verification */
    verificationEnabled?: boolean;
    /** Maximum retry attempts */
    maxRetries?: number;
    /** Working directory override */
    workingDirectory?: string;
    /** Sandbox paths (allowed directories) */
    sandboxPaths?: string[];
    /** Debug logging */
    debug?: boolean;
    /** Per-executor user preferences */
    executorPreferences?: Partial<Record<ExecutorType, ExecutorPreference>>;
}

/**
 * User preference for a specific executor
 */
export interface ExecutorPreference {
    /** User has disabled this executor */
    disabled?: boolean;
    /** Custom binary path override */
    path?: string;
    /** Session timeout override (ms) */
    sessionTimeout?: number;
    /** Additional CLI arguments */
    extraArgs?: string[];
}

// ============================================
// Config File Handling
// ============================================

/**
 * Get the Worker config file path
 * Uses JSON format for consistency with MCP config pattern
 */
export function getWorkerConfigPath(): string {
    return path.join(getConfigDir(), 'worker.json');
}

/**
 * Load Worker config from file (JSON format)
 */
export function loadWorkerConfig(): WorkerConfigFile | undefined {
    const configPath = getWorkerConfigPath();

    if (!fs.existsSync(configPath)) {
        return undefined;
    }

    try {
        const content = fs.readFileSync(configPath, 'utf-8');
        return JSON.parse(content) as WorkerConfigFile;
    } catch (error) {
        console.error(`Failed to load worker config: ${error}`);
        return undefined;
    }
}

/**
 * Save Worker config to file (JSON format)
 */
export function saveWorkerConfig(config: WorkerConfigFile): void {
    const configPath = getWorkerConfigPath();
    const paeanDir = path.dirname(configPath);

    // Ensure .paean directory exists
    if (!fs.existsSync(paeanDir)) {
        fs.mkdirSync(paeanDir, { recursive: true });
    }

    try {
        const content = JSON.stringify(config, null, 2);
        fs.writeFileSync(configPath, content, 'utf-8');
    } catch (error) {
        console.error(`Failed to save worker config: ${error}`);
        throw error;
    }
}

/**
 * Create default Worker config file if it doesn't exist
 */
export function initWorkerConfigIfNeeded(): WorkerConfigFile {
    const configPath = getWorkerConfigPath();

    if (fs.existsSync(configPath)) {
        return loadWorkerConfig() || createDefaultConfig();
    }

    const defaultConfig = createDefaultConfig();
    saveWorkerConfig(defaultConfig);
    return defaultConfig;
}

/**
 * Create default config
 */
function createDefaultConfig(): WorkerConfigFile {
    return {
        enabled: true,
        capabilities: ['shell', 'filesystem', 'git'],
        pollInterval: 30000,
        taskTimeout: 1800000, // 30 minutes
        autonomousMode: false,
        verificationEnabled: true,
        maxRetries: 3,
        workingDirectory: homedir(),
        sandboxPaths: [
            homedir(),
            '/tmp',
        ],
        debug: false,
    };
}

/**
 * Merge file config with provided overrides
 */
export function mergeWorkerConfig(overrides?: Partial<WorkerConfig>): WorkerConfig {
    const fileConfig = loadWorkerConfig() || createDefaultConfig();

    return {
        pollInterval: overrides?.pollInterval ?? fileConfig.pollInterval ?? 30000,
        taskTimeout: overrides?.taskTimeout ?? fileConfig.taskTimeout ?? 1800000,
        maxRetries: overrides?.maxRetries ?? fileConfig.maxRetries ?? 3,
        cooldownOnError: 60000, // Fixed value
        autonomousMode: overrides?.autonomousMode ?? fileConfig.autonomousMode ?? false,
        verificationEnabled: overrides?.verificationEnabled ?? fileConfig.verificationEnabled ?? true,
        debug: overrides?.debug ?? fileConfig.debug ?? false,
    };
}

// ============================================
// Sandbox Path Validation
// ============================================

/**
 * Check if a path is within allowed sandbox paths
 */
export function isPathInSandbox(targetPath: string, sandboxPaths?: string[]): boolean {
    if (!sandboxPaths || sandboxPaths.length === 0) {
        return true; // No restrictions
    }

    const absolutePath = path.resolve(targetPath);

    return sandboxPaths.some(sandboxPath => {
        const absoluteSandbox = path.resolve(sandboxPath);
        return absolutePath.startsWith(absoluteSandbox);
    });
}

/**
 * Get sandbox paths from config
 */
export function getSandboxPaths(): string[] {
    const config = loadWorkerConfig();
    return config?.sandboxPaths || [homedir(), '/tmp'];
}

/**
 * Validate and sanitize a path against sandbox restrictions
 * Returns the path if valid, throws if outside sandbox
 */
export function validateSandboxPath(targetPath: string): string {
    const sandboxPaths = getSandboxPaths();

    if (!isPathInSandbox(targetPath, sandboxPaths)) {
        throw new Error(`Path '${targetPath}' is outside allowed sandbox paths: ${sandboxPaths.join(', ')}`);
    }

    return path.resolve(targetPath);
}

// ============================================
// Executor Preference Management
// ============================================

/**
 * Get preference for a specific executor
 */
export function getExecutorPreference(type: ExecutorType): ExecutorPreference | undefined {
    const config = loadWorkerConfig();
    return config?.executorPreferences?.[type];
}

/**
 * Set preference for a specific executor
 */
export function setExecutorPreference(type: ExecutorType, pref: ExecutorPreference): void {
    const config = loadWorkerConfig() || createDefaultConfig();
    if (!config.executorPreferences) {
        config.executorPreferences = {};
    }
    config.executorPreferences[type] = {
        ...config.executorPreferences[type],
        ...pref,
    };
    saveWorkerConfig(config);
}

/**
 * Check if user has disabled a specific executor
 */
export function isExecutorUserDisabled(type: ExecutorType): boolean {
    const pref = getExecutorPreference(type);
    return pref?.disabled === true;
}

/**
 * Enable or disable an executor for the current user
 */
export function setExecutorEnabled(type: ExecutorType, enabled: boolean): void {
    setExecutorPreference(type, { disabled: !enabled });
}

/**
 * Get all executor preferences
 */
export function getAllExecutorPreferences(): Partial<Record<ExecutorType, ExecutorPreference>> {
    const config = loadWorkerConfig();
    return config?.executorPreferences || {};
}
